import { useEffect, useRef, useState, type ReactNode } from "react";
import { useMatches, useRouterState } from "@tanstack/react-router";
import {
  Button as AriaButton,
  Dialog,
  Modal,
  ModalOverlay,
} from "react-aria-components";
import { useTranslation } from "react-i18next";
import { Icon, images } from "@core/assets";
import { useUIStore } from "@core/stores";
import { cn } from "@core/utils";
import { Breadcrumb } from "./Breadcrumb";
import { FrameSidebar } from "./FrameSidebar";
import type { NavGroup } from "./types";

interface AppFrameProps {
  /** Labelled nav groups, already filtered to what this account may see. */
  nav: NavGroup[];
  /** The top bar's right side: the user menu. */
  topBarEnd: ReactNode;
  children: ReactNode;
}

const MAIN_ID = "main-content";

/**
 * Keeps a single-page app honest for keyboard and screen-reader users after each navigation:
 * focus moves to the content (a page load would do it for free; a client-side route change does
 * not) and the document title follows the page. Not on the first render, so a fresh load keeps
 * the browser's own behaviour.
 */
function useRouteAnnouncements(mainRef: React.RefObject<HTMLElement | null>) {
  const { t } = useTranslation("common");
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const matches = useMatches();
  // The path we last announced. Comparing paths (not "is this the first run") keeps a dev-only
  // double run of effects from stealing focus on the very first load.
  const announcedPath = useRef(pathname);

  const titleKey = matches
    .map((match) => match.staticData.title)
    .findLast((key) => key !== undefined);
  const title = titleKey ? `${t(titleKey)} · DZ Champions` : "DZ Champions";

  useEffect(() => {
    document.title = title;
  }, [title]);

  useEffect(() => {
    if (announcedPath.current === pathname) return;
    announcedPath.current = pathname;
    mainRef.current?.focus({ preventScroll: true });
  }, [pathname, mainRef]);
}

/**
 * The signed-in frame (Figma, Team and Cohortes frames): a grey page, a sidebar on the left, and
 * the content as a rounded card with a top bar over the page body. Presentational and
 * domain-free: the navigation and the user menu arrive as props, so core never learns what the
 * app's routes or roles are. An area (see `areas/staff`) composes it.
 *
 * One component tree for every width: from `lg` the sidebar is permanent and collapses to an
 * icon rail (remembered in the `ui` store); below it a top bar opens the same sidebar as an
 * off-canvas drawer (React Aria `Modal`: focus trap, Escape, scroll lock, focus return).
 *
 * Accessibility: a skip link, one `main` that takes focus after each navigation, and a document
 * title that follows the page.
 */
export function AppFrame({ nav, topBarEnd, children }: AppFrameProps) {
  const { t } = useTranslation("common");
  const collapsed = useUIStore((state) => state.sidebarCollapsed);
  const toggleSidebar = useUIStore((state) => state.toggleSidebar);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);

  useRouteAnnouncements(mainRef);

  return (
    <div className="flex min-h-screen bg-ink-100 lg:h-screen lg:py-xs lg:pr-xs">
      <a
        href={`#${MAIN_ID}`}
        className={
          "sr-only focus:not-sr-only focus:fixed focus:top-xs focus:left-xs focus:z-50 " +
          "focus:rounded-md focus:bg-surface-card focus:px-md focus:py-sm focus:text-sm-medium " +
          "focus:text-(--text-primary) focus:shadow-menu focus:ring-2 focus:ring-focus-ring"
        }
      >
        {t("layout.skip-to-content")}
      </a>

      {/* Permanent sidebar, from lg. Width eases between the full sidebar and the rail. */}
      <aside
        className={cn(
          "hidden shrink-0 transition-[width] duration-200 motion-reduce:transition-none lg:block",
          collapsed ? "w-16" : "w-60"
        )}
      >
        <FrameSidebar
          groups={nav}
          collapsed={collapsed}
          onToggleCollapsed={toggleSidebar}
        />
      </aside>

      {/* Off-canvas drawer, below lg. */}
      <ModalOverlay
        isOpen={isMenuOpen}
        onOpenChange={setMenuOpen}
        isDismissable
        className="fixed inset-0 z-50 flex bg-overlay entering:animate-in entering:fade-in exiting:animate-out exiting:fade-out lg:hidden"
      >
        <Modal className="h-full w-70 max-w-[85%] bg-ink-100 shadow-lg">
          <Dialog
            aria-label={t("layout.main-navigation")}
            className="relative h-full outline-none"
          >
            <AriaButton
              onPress={() => setMenuOpen(false)}
              aria-label={t("layout.close-menu")}
              className={
                "absolute top-xs right-xs z-10 flex size-11 items-center justify-center rounded-md " +
                "text-(--text-secondary) outline-none transition-colors hover:bg-ink-50 " +
                "focus-visible:ring-2 focus-visible:ring-focus-ring"
              }
            >
              <Icon name="close" size={20} />
            </AriaButton>
            <FrameSidebar groups={nav} onNavigate={() => setMenuOpen(false)} />
          </Dialog>
        </Modal>
      </ModalOverlay>

      {/* `min-w-0` is load-bearing: without it a wide child stretches this column instead of
          scrolling inside it. The card look is a desktop treatment; on small screens the content
          runs edge to edge. */}
      <div
        className={
          "flex min-w-0 flex-1 flex-col bg-ink-25 " +
          "lg:overflow-hidden lg:rounded-xl lg:border lg:border-(--border-default)"
        }
      >
        <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-xs border-b border-(--border-default) bg-ink-25 px-md lg:static lg:px-xl">
          <AriaButton
            onPress={() => setMenuOpen(true)}
            aria-label={t("layout.open-menu")}
            className={
              "flex size-11 shrink-0 items-center justify-center rounded-md outline-none lg:hidden " +
              "text-(--text-secondary) transition-colors hover:bg-ink-100 " +
              "focus-visible:ring-2 focus-visible:ring-focus-ring"
            }
          >
            <Icon name="menu" size={24} />
          </AriaButton>
          <img
            src={images["logo-primary"]}
            alt="DZ Champions"
            className="h-7 w-auto lg:hidden"
          />
          <div className="hidden min-w-0 flex-1 lg:block">
            <Breadcrumb />
          </div>
          <div className="ml-auto lg:ml-0">{topBarEnd}</div>
        </header>

        {/* `tabIndex={-1}` makes the content focusable by script only: focus lands here after a
            route change without adding a tab stop. */}
        <main
          id={MAIN_ID}
          ref={mainRef}
          tabIndex={-1}
          className="flex flex-1 flex-col gap-xl px-md py-xl outline-none sm:px-xl lg:overflow-y-auto lg:px-4xl lg:py-3xl"
        >
          {children}
        </main>
      </div>
    </div>
  );
}
